import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  ArrowRight,
  Play,
  Shield,
  Monitor,
  Smartphone,
  Globe,
  CalendarDays,
} from "lucide-react";
import Reveal from "@/components/landing/Reveal";

export default function Hero() {
  const { t } = useTranslation();

  return (
    <section className="relative min-h-[calc(100vh-4rem)] overflow-hidden border-b border-sidebar-border bg-[linear-gradient(130deg,hsl(var(--sidebar-background))_0%,hsl(var(--foreground))_100%)]">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(1000px_500px_at_5%_-10%,hsl(var(--accent)/0.22),transparent)]" />
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(900px_420px_at_95%_0%,hsl(var(--chart-2)/0.18),transparent)]" />

      <div className="container relative mx-auto flex min-h-[calc(100vh-4rem)] items-center px-4 py-10 lg:px-8 lg:py-14">
        <div className="grid items-start gap-12 lg:grid-cols-2">
          <Reveal direction="up">
            <div className="motion-safe:transition-all motion-safe:duration-300 motion-safe:ease-out">
              <h1 className="mb-2 min-h-[3.1em] text-4xl font-extrabold leading-[1.26] tracking-tight text-background md:min-h-[2.8em] md:text-5xl lg:min-h-[2.7em] lg:text-6xl">
                {t("landing.hero.titleRest")}
                <span className="text-accent">
                  {t("landing.hero.titleAccent")}
                </span>
              </h1>
              <p className="mt-6 min-h-[5.5rem] max-w-lg text-lg leading-relaxed text-background/75 md:min-h-[4.5rem] lg:min-h-[6rem]">
                {t("landing.hero.copy")}
              </p>
              <div className="mt-8 flex flex-wrap gap-4">
                <Button
                  size="lg"
                  className="gap-2 bg-accent text-base text-accent-foreground hover:bg-accent/90"
                  asChild
                >
                  <Link to="/pricing">
                    {t("landing.hero.startNow")}{" "}
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="gap-2 border-background/30 bg-transparent text-base text-background hover:bg-background/10 hover:text-background"
                  asChild
                >
                  <a href="#demo">
                    <Play className="h-4 w-4" /> {t("landing.hero.watchDemo")}
                  </a>
                </Button>
              </div>
              <div className="mt-6 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-background/70">
                <span className="flex items-center gap-1.5">
                  <Shield className="h-4 w-4" />{" "}
                  {t("landing.hero.secureApprovals")}
                </span>
                <span className="flex items-center gap-1.5">
                  <Monitor className="h-4 w-4" /> {t("landing.hero.desktop")}
                </span>
                <span className="flex items-center gap-1.5">
                  <Smartphone className="h-4 w-4" />{" "}
                  {t("landing.hero.mobileApp")}
                </span>
                <span className="flex items-center gap-1.5">
                  <CalendarDays className="h-4 w-4" />{" "}
                  {t("landing.hero.scheduling")}
                </span>
                <span className="flex items-center gap-1.5">
                  <Globe className="h-4 w-4" /> {t("landing.hero.bilingual")}
                </span>
              </div>
            </div>
          </Reveal>

          <Reveal direction="right" delayMs={200} className="lg:self-center">
            <div
              className="relative motion-safe:transition-all motion-safe:duration-300 motion-safe:ease-out"
              role="img"
              aria-label="WorkScope approval summary with client totals and approval actions"
            >
              <div className="rounded-xl border border-background/20 bg-background/10 p-1.5 shadow-2xl backdrop-blur-sm">
                <div className="rounded-lg bg-background p-6">
                  <div className="mb-4 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="h-3 w-3 rounded-full bg-accent" />
                      <span className="text-sm font-semibold text-foreground">
                        {t("landing.hero.cardTitle")}
                      </span>
                    </div>
                    <span className="rounded-full bg-accent px-3 py-1 text-xs font-medium text-accent-foreground">
                      {t("landing.hero.cardStatus")}
                    </span>
                  </div>
                  <div className="space-y-3">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">
                        {t("landing.hero.cardClient")}
                      </span>
                      <span className="font-medium text-foreground">
                        Riverside Construction
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">
                        {t("landing.hero.cardLabor")}
                      </span>
                      <span className="font-medium text-foreground">
                        $2,450.00
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">
                        {t("landing.hero.cardMaterials")}
                      </span>
                      <span className="font-medium text-foreground">
                        $1,280.00
                      </span>
                    </div>
                    <div className="flex justify-between border-t border-border pt-3 text-sm font-semibold">
                      <span>{t("landing.hero.cardTotal")}</span>
                      <span className="text-accent">$3,730.00</span>
                    </div>
                  </div>
                  <div className="mt-4 grid grid-cols-3 gap-2">
                    <div className="rounded-md bg-emerald-500/10 py-2 text-center text-xs font-semibold text-emerald-600">
                      {t("landing.hero.cardApprove")}
                    </div>
                    <div className="rounded-md bg-red-500/10 py-2 text-center text-xs font-semibold text-red-500">
                      {t("landing.hero.cardReject")}
                    </div>
                    <div className="rounded-md bg-muted py-2 text-center text-xs font-semibold text-muted-foreground">
                      {t("landing.hero.cardClarify")}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
